import { configureStore, combineReducers } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // Defaults to local storage
import authReducer from './slices/authSlice';
import vocabularyReducer from './slices/vocabulary'; // Updated import to use modular vocabulary
import exerciseReducer from './slices/exerciseSlice';
import dashboardReducer from './slices/dashboardSlice';
import achievementsReducer from './slices/achievementsSlice';
import uiReducer from './slices/uiSlice';
import lessonsReducer from './slices/lessonsSlice';
import conversationReducer from './slices/conversationSlice';

import sessionReducer from './slices/sessionSlice';
import testingReducer from './slices/testingSlice';
import speakingReducer from './slices/speaking';
import readingReducer from './slices/reading';
import progressReducer from './slices/progressSlice';
import writingReducer from './slices/writing/writingSlice';
// Learning Progress slices
import { learningProgressSlice, weeklyPlanSlice, dailyScheduleSlice } from './slices/learningProgress';
// Conversation slices
import { conversationSessionSlice, conversationMessageSlice, conversationProgressSlice, aiConversationSessionSlice } from './slices/conversation';
// Student Progress slices
import { studentProgressSlice } from './slices/studentProgress';

import rootSaga from './sagas';
import modalReducer from './slices/modalSlice';
import listeningReducer from './slices/listening/listeningSlice';
import grammarReducer from './slices/grammar/grammarSlice';
import syllabusReducer from './slices/syllabusSlice';

import commonReducer from './slices/commonSlice';
import avatarReducer from './slices/avatarSlice';

// Create the saga middleware
const sagaMiddleware = createSagaMiddleware();

// Combine all reducers
const rootReducer = combineReducers({
    auth: authReducer,
    vocabulary: vocabularyReducer,
    exercise: exerciseReducer,
    dashboard: dashboardReducer,
    achievements: achievementsReducer,
    ui: uiReducer,
    lessons: lessonsReducer,
    conversation: conversationReducer,

    session: sessionReducer,
    testing: testingReducer,
    speaking: speakingReducer,
    reading: readingReducer,
    progress: progressReducer,
    writing: writingReducer,
    modal: modalReducer,
    // Learning Progress reducers
    learningProgress: learningProgressSlice,
    weeklyPlan: weeklyPlanSlice,
    dailySchedule: dailyScheduleSlice,
    // New conversation reducers
    conversationSession: conversationSessionSlice,
    conversationMessage: conversationMessageSlice,
    conversationProgress: conversationProgressSlice,
    aiConversationSession: aiConversationSessionSlice,

    studentProgress: studentProgressSlice,
    listening: listeningReducer,
    grammar: grammarReducer,
    syllabus: syllabusReducer,

    common: commonReducer,
    avatar: avatarReducer,
});

// Persist configuration
const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['ui', 'auth', 'avatar'], // Persist state
    blacklist: [
        'vocabulary',
        'exercise',
        'dashboard',
        'achievements',
        'lessons',
        'conversation',

        'session',
        'speaking',
        'speakingSessions',
        'phonemes',
        'speakingAnalytics',
        'reading',
        'modal',
        'learningProgress',
        'weeklyPlan',
        'dailySchedule',
        'conversationSession',
        'conversationMessage',
        'conversationProgress',
        'aiConversationSession',
        'aiConversation',
    ], // Don't persist these
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store with persisted reducer
export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            thunk: true, // Enable thunk for achievements slice
            serializableCheck: false,
        }).concat(sagaMiddleware),
    devTools: process.env.NODE_ENV !== 'production',
});

// Run the root saga
sagaMiddleware.run(rootSaga);

// Create persistor
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export default store;
